import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getExercise, createExercise, updateExercise } from '../api/exercises';
import { getTags } from '../api/tags';
import { suggestExercise } from '../api/ai';
import { addMediaToExercise, removeMediaFromExercise } from '../api/media';
import { useAuth } from '../context/AuthContext';
import ExerciseMediaSection from '../components/ExerciseMediaSection';
import TagSelector from '../components/TagSelector';
import AlertBanner from '../components/common/AlertBanner';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmModal from '../components/common/ConfirmModal';

export default function ExerciseFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [form, setForm] = useState({
    name: '',
    description: '',
    difficulty: 1,
    equipment_needed: '',
    instructions: '',
    tag_ids: [],
    is_global: false,
  });
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [existingMedia, setExistingMedia] = useState([]);
  const [pendingMedia, setPendingMedia] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const promises = [getTags()];
    if (isEdit) promises.push(getExercise(id));
    Promise.all(promises)
      .then(([t, ex]) => {
        setTags(t);
        if (ex) {
          setForm({
            name: ex.name || '',
            description: ex.description || '',
            difficulty: ex.difficulty || 1,
            equipment_needed: ex.equipment_needed || '',
            instructions: ex.instructions || '',
            tag_ids: ex.tags?.map((t) => t.id) || [],
            is_global: ex.is_global || false,
          });
          setExistingMedia(ex.media || []);
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // Browser-Schließen / Tab-Wechsel warnen
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  function safeNavigate(path) {
    if (isDirty) { setShowLeaveConfirm(true); return; }
    navigate(path);
  }

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  }

  function toggleTag(tagId) {
    setForm((prev) => ({
      ...prev,
      tag_ids: prev.tag_ids.includes(tagId)
        ? prev.tag_ids.filter((t) => t !== tagId)
        : [...prev.tag_ids, tagId],
    }));
    setIsDirty(true);
  }

  async function handleAiSuggest() {
    if (!form.name.trim()) {
      setError('Bitte zuerst einen Namen eingeben, damit die KI einen Vorschlag erstellen kann.');
      return;
    }
    setAiLoading(true);
    setError('');
    try {
      const result = await suggestExercise(form.name, form.difficulty, form.equipment_needed);
      setForm((prev) => ({
        ...prev,
        description: result.description,
        instructions: result.instructions,
      }));
      setAiGenerated(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setAiLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      let exerciseId = id;
      if (isEdit) {
        await updateExercise(id, form);
      } else {
        const created = await createExercise(form);
        exerciseId = created.id;
      }
      // Ausstehende Medien der Übung zuordnen
      for (const item of pendingMedia) {
        await addMediaToExercise(exerciseId, item.media.id, item.mediaType);
      }
      setIsDirty(false);
      navigate('/exercises');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div style={{ maxWidth: 680 }}>
      <div className="d-flex align-items-center gap-2 mb-4">
        <button className="btn btn-outline-secondary btn-sm" onClick={() => safeNavigate('/exercises')}>
          <i className="bi bi-arrow-left" />
        </button>
        <h4 className="mb-0 fw-bold">
          {isEdit ? 'Übung bearbeiten' : 'Neue Übung'}
        </h4>
      </div>

      <AlertBanner message={error} type="danger" onDismiss={() => setError('')} />

      <form onSubmit={handleSubmit}>
        <div className="card mb-3">
          <div className="card-body">
            <h6 className="card-subtitle mb-3 text-muted">Grundinformationen</h6>
            <div className="mb-3">
              <label className="form-label">Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                required
              />
            </div>
            <div className="row g-3 mb-0">
              <div className="col-sm-4">
                <label className="form-label">Schwierigkeit</label>
                <select
                  className="form-select"
                  value={form.difficulty}
                  onChange={(e) => set('difficulty', Number(e.target.value))}
                >
                  {[1, 2, 3, 4, 5].map((d) => (
                    <option key={d} value={d}>{d} – {['Sehr leicht', 'Leicht', 'Mittel', 'Schwer', 'Sehr schwer'][d - 1]}</option>
                  ))}
                </select>
              </div>
              <div className="col-sm-8">
                <label className="form-label">Equipment</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="z.B. Hantelbank, Kettlebell"
                  value={form.equipment_needed}
                  onChange={(e) => set('equipment_needed', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* KI-Vorschlag Button */}
        <div className="d-grid d-sm-flex justify-content-sm-end mb-3">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleAiSuggest}
            disabled={aiLoading}
            title="Beschreibung und Anleitung von Mistral AI generieren lassen"
          >
            {aiLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                KI generiert…
              </>
            ) : (
              <>
                <i className="bi bi-stars me-2" />
                KI-Vorschlag generieren
              </>
            )}
          </button>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="card-subtitle text-muted mb-0">Beschreibung</h6>
              {aiGenerated && (
                <span className="badge bg-success-subtle text-success small">
                  <i className="bi bi-check me-1" />KI-generiert
                </span>
              )}
            </div>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Kurze Beschreibung der Übung…"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
            />
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="card-subtitle text-muted mb-0">Anleitung</h6>
              {aiGenerated && (
                <span className="badge bg-success-subtle text-success small">
                  <i className="bi bi-check me-1" />KI-generiert
                </span>
              )}
            </div>
            <textarea
              className="form-control"
              rows={6}
              placeholder="Schritt-für-Schritt-Anleitung zur Ausführung…"
              value={form.instructions}
              onChange={(e) => set('instructions', e.target.value)}
            />
          </div>
        </div>

        {isSuperAdmin && (
          <div className="card mb-3">
            <div className="card-body">
              <div className="form-check form-switch">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="isGlobal"
                  checked={form.is_global}
                  onChange={(e) => set('is_global', e.target.checked)}
                />
                <label className="form-check-label fw-semibold" htmlFor="isGlobal">
                  <i className="bi bi-globe me-1 text-warning" />
                  Global veröffentlichen
                </label>
                <div className="form-text">Alle Mandanten sehen diese Übung und können sie importieren.</div>
              </div>
            </div>
          </div>
        )}

        {!isSuperAdmin && (
        <div className="card mb-4">
          <div className="card-body">
            <h6 className="card-subtitle mb-3 text-muted">Tags</h6>
            <TagSelector
              tags={tags}
              selectedIds={form.tag_ids}
              onToggle={toggleTag}
              onTagCreated={(tag) => setTags((prev) => [...prev, tag])}
            />
          </div>
        </div>
        )}

        <ExerciseMediaSection
          exerciseName={form.name}
          existingMedia={existingMedia}
          pendingMedia={pendingMedia}
          onAddPending={(item) => { setPendingMedia((prev) => [...prev, item]); setIsDirty(true); }}
          onRemoveExisting={async (mediaRelId) => {
            try {
              await removeMediaFromExercise(id, mediaRelId);
              setExistingMedia((prev) => prev.filter((m) => m.id !== mediaRelId));
              setIsDirty(true);
            } catch (e) {
              setError(e.message);
            }
          }}
          onRemovePending={(mediaId) => {
            setPendingMedia((prev) => prev.filter((p) => p.media.id !== mediaId));
            setIsDirty(true);
          }}
        />

        <div className="d-grid d-sm-flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? <><span className="spinner-border spinner-border-sm me-2" />Speichern…</> : 'Speichern'}
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={() => safeNavigate('/exercises')}>
            Abbrechen
          </button>
        </div>
      </form>

      <ConfirmModal
        show={showLeaveConfirm}
        title="Ungespeicherte Änderungen"
        message="Du hast ungespeicherte Änderungen. Wenn du jetzt verlässt, gehen diese verloren."
        confirmLabel="Verlassen"
        confirmClass="btn-warning"
        cancelLabel="Zurück zum Bearbeiten"
        onConfirm={() => { setShowLeaveConfirm(false); setIsDirty(false); navigate('/exercises'); }}
        onCancel={() => setShowLeaveConfirm(false)}
      />
    </div>
  );
}
